
import React, { useState, useEffect, useRef } from 'react'
import {RoundTrackContainer, ProgressBar, Container, ProgressCircle} from './EPCircularTrack.styles'
import PropTypes from 'prop-types'
import { getAngleFromPercentage, getPercentageFromAngle } from './utils'
import { theme } from '../../theme'

const preventDefaultScroll = (event) => {
    event.preventDefault()
    event.stopPropagation()
}

const EPCircularTrack = ({accuracy, onChange, value, size, strokeWidth, backgroundColor, progressRadius, progressColor, progressBarColor}) => {
    const radius = size / 2
    const roundTrackEl = useRef()
    const progressBarDasharray = 2 * Math.PI * radius
    const defaultAngle = getAngleFromPercentage(value)
    const [mouseDown, setMouseDown] = useState(false)
    const progressBarPosition = {
        x: radius * Math.cos(defaultAngle) + radius,
        y: radius * Math.sin(defaultAngle) + radius,
    }
    const strokeDashoffset = ((100 - getPercentageFromAngle(defaultAngle)) * progressBarDasharray) / 100

    const updatePosition = (x, y) => {
        const rect = roundTrackEl.current.getBoundingClientRect()
        const dy = y - (radius + rect.top)
        const dx = x - (radius + rect.left)
        const theta = Math.atan2(dy, dx)
        const newPercentage = getPercentageFromAngle(theta)
        const newValue = newPercentage/100
        if (((newValue - accuracy) < value) ||
        ((newValue + accuracy) > value)) {
            onChange(newValue)
        }
    }

    const onTouchStart = (v) => {
        const position = v.changedTouches[0]
        setMouseDown(true)
        updatePosition(position.clientX, position.clientY)
        preventDefaultScroll(v)
    }

    const onClickDown = (v) => {
        setMouseDown(true)
        updatePosition(v.clientX, v.clientY)
    }

    const onTouchEnd = (v) => {
        const position = v.changedTouches[0]
        setMouseDown(false)
        updatePosition(position.clientX, position.clientY)
        preventDefaultScroll(v)
    }

    const onClickUp = (v) => {
        setMouseDown(false)
        updatePosition(v.clientX, v.clientY)
    }

    const onTouchMove = (v) => {
        if (mouseDown){
            const position = v.changedTouches[0]
            updatePosition(position.clientX, position.clientY)
        }
        preventDefaultScroll(v)
    }

    useEffect(() => {
        document && document.addEventListener('touchmove', preventDefaultScroll, { passive: false })
        return () => {
            document && document.removeEventListener('touchmove', preventDefaultScroll)
        }
    }, [])

    const onClickDrag = (v) => {
        if (mouseDown){
            updatePosition(v.clientX, v.clientY)
        }
    }

    return<Container size={size} height={`${size}px`} width={`${size}px`}>
        <RoundTrackContainer ref={roundTrackEl} strokeWidth={strokeWidth} color={backgroundColor} r={radius} cx={radius} cy={radius} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchMove={onTouchMove} onMouseDown={onClickDown} onMouseUp={onClickUp} onMouseMove={onClickDrag} />
        <ProgressBar  size={radius*2} r={radius} strokeWidth={strokeWidth} strokeDasharray={progressBarDasharray}
            strokeDashoffset={strokeDashoffset === progressBarDasharray ? 0 : strokeDashoffset}
            color={progressBarColor} cx={radius} cy={radius} />
        <ProgressCircle r={progressRadius} progressColor={progressColor} cx={progressBarPosition.x} cy={progressBarPosition.y} />
    </Container>
}

EPCircularTrack.propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.number,
    progressColor: PropTypes.string,
    size: PropTypes.number,
    progressRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    progressBarColor: PropTypes.string,
    strokeWidth: PropTypes.number,
    accuracy: PropTypes.number,
}

EPCircularTrack.defaultProps = {
    onChange: () => {},
    value: 0,
    size: 200,
    progressRadius: 33,
    progressColor: '#FFFFFF',
    backgroundColor: theme.colors['main'],
    progressBarColor: theme.colors['main'],
    strokeWidth: 80,
    accuracy: 0,
}

export default EPCircularTrack
